import React, { useCallback, useContext, useState } from "react";

//packages
import { useTranslation } from "react-i18next";
import classNames from "classnames";

// componetnts
import Modal from "../../common/Modal";
import PrimaryButton from "../../common/PrimaryButton";

// styles
import styles from "./brokerInvite.module.scss";

// context provider
import { ContractContext } from "../../../providers/contract";

interface Props {
  showModal: boolean;
  closeModal: () => void;
  userType: "seller" | "buyer";
}

const BrokerInviteModal = ({ showModal, closeModal, userType }: Props) => {
  const { t } = useTranslation();
  const { contract, actions } = useContext(ContractContext);
  const [copied, setCoppied] = useState(false);

  const fetchContractUrl = useCallback(
    (useShortId?: boolean) => {
      const url = location.origin;
      return `${url}/contract/${
        useShortId ? contract?.shortId : contract?.id
      }?userType=${userType}`;
    },
    [contract?.id, contract?.shortId, userType]
  );

  const onCopy = useCallback(async () => {
    try {
      await navigator?.clipboard?.writeText(
        `${t("lbl_message", {
          val1: contract?.partiesProfiles?.BROKER?.[0]?.profile?.fullName,
          val2: contract?.itemDetail?.title,
        })}\n${t("lbl_join_contract")}${fetchContractUrl()}`
      );
      actions?.showSnackbar(`${t("COPIED")}`, "success");
      setCoppied(true);
    } catch (error) {
      actions?.showSnackbar(`${t("COPIED")}`, "error");
      console.error(error);
    }
  }, [
    actions,
    contract?.itemDetail?.title,
    contract?.partiesProfiles?.BROKER,
    fetchContractUrl,
    t,
  ]);

  return (
    <Modal size={"small"} visible={showModal} onCancel={closeModal}>
      <div className={styles["container"]}>
        <div className={styles["image_container"]}>
          <img
            className={styles["avatar_img"]}
            src="/assets/images/svg/emailInvite.svg"
          />
        </div>
        <h2>
          {userType === "buyer"
            ? `${t("lbl_send_buyer_invite")}`
            : `${t("lbl_send_seller_invite")}`}
        </h2>
        <div className={styles["invite_text"]}>
          <p className={classNames(styles["p2"], styles["g400"])}>
            {userType === "buyer"
              ? `${t("lbl_send_invite_desc_buyer")}`
              : `${t("lbl_send_invite_desc_seller")}`}
          </p>
        </div>
        <div className={styles["text_container"]}>
          <p
            className={classNames(
              styles["g800"],
              styles["subtitle5"],
              styles["info_header"]
            )}>
            {t("lbl_message", {
              val1: contract?.partiesProfiles?.BROKER?.[0]?.profile?.fullName,
              val2: contract?.itemDetail?.title,
            })}
          </p>
          <p
            dir="rtl"
            className={styles["p3"]}
            style={{ marginBottom: 0, marginTop: "8px" }}>
            {t("lbl_join_contract")}
          </p>
          <div
            onClick={onCopy}
            className={classNames(styles["link_container"], styles["pointer"])}>
            <a href={fetchContractUrl()} style={{ wordBreak: "break-all" }}>
              {fetchContractUrl(true)?.split("?userType=")?.[0]}
            </a>
          </div>
        </div>

        <div className={styles["copy_button"]}>
          <PrimaryButton
            onClick={onCopy}
            id={`create_contract_success_copy_link`}>
            {copied ? t("INVITE_COPIED") : t("COPY_INVITE")}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default BrokerInviteModal;
